import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Building2, 
  TrendingUp, 
  Euro, 
  MousePointerClick,
  Eye,
  Target,
  Clock,
  CheckCircle,
  Plus,
  BarChart3,
  Loader2,
  AlertTriangle,
  FileSignature,
  Send,
  Phone,
  Mail,
  Shield,
  Globe,
  MapPin,
  Image,
  Link2,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const B2BAdvertising = () => {
  const { user, token } = useAuth();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [pricing, setPricing] = useState(null);
  
  // Modals
  const [showCreateContractModal, setShowCreateContractModal] = useState(false);
  const [showCreateBannerModal, setShowCreateBannerModal] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  
  // Form states
  const [newContract, setNewContract] = useState({
    campaign_name: '',
    total_budget: 100,
    cost_per_click: 0.15,
    start_date: '',
    end_date: '',
    language: 'fr',
    banner_positions: ['top', 'sidebar'],
    target_countries: []
  });
  
  const [newBanner, setNewBanner] = useState({
    title: { fr: '', en: '' },
    content: { fr: '', en: '' },
    link_url: '',
    position: 'top',
    target_countries: [],
    ethics_declaration: false
  });
  
  const [selectedContract, setSelectedContract] = useState(null);
  const [contractStats, setContractStats] = useState(null);
  const [signatureStep, setSignatureStep] = useState(1);
  const [signatureData, setSignatureData] = useState({ phone: '', sms_code: '', email_code: '' });
  const [signingLoading, setSigningLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [contractsRes, bannersRes, pricingRes] = await Promise.all([
        axios.get(`${API_URL}/api/advertising/contracts/my`, { headers }).catch(() => ({ data: { contracts: [] } })),
        axios.get(`${API_URL}/api/advertising/banners/my`, { headers }).catch(() => ({ data: { banners: [] } })),
        axios.get(`${API_URL}/api/advertising/pricing`).catch(() => ({ data: null }))
      ]);
      
      setContracts(contractsRes.data.contracts || []);
      setBanners(bannersRes.data.banners || []);
      setPricing(pricingRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
    setLoading(false);
  };

  const handleCreateContract = async () => {
    if (!newContract.campaign_name || newContract.total_budget < 50) {
      toast.error('Nom de campagne requis et budget minimum 50€');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/advertising/contracts/create`, newContract, { headers });
      toast.success('Contrat créé avec succès !');
      setShowCreateContractModal(false);
      setNewContract({
        campaign_name: '',
        total_budget: 100,
        cost_per_click: 0.15,
        start_date: '',
        end_date: '',
        language: 'fr',
        banner_positions: ['top', 'sidebar'],
        target_countries: []
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleCreateBanner = async () => {
    if (!newBanner.title.fr || !newBanner.link_url || !newBanner.ethics_declaration) {
      toast.error('Titre, lien et déclaration éthique requis');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/advertising/banners/create`, newBanner, { headers });
      toast.success('Bannière créée ! En attente de validation.');
      setShowCreateBannerModal(false);
      setNewBanner({
        title: { fr: '', en: '' },
        content: { fr: '', en: '' },
        link_url: '',
        position: 'top',
        target_countries: [],
        ethics_declaration: false
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const initiateSignature = async () => {
    if (!signatureData.phone) {
      toast.error('Numéro de téléphone requis');
      return;
    }
    
    setSigningLoading(true);
    try {
      await axios.post(`${API_URL}/api/advertising/contracts/initiate-signature`, {
        contract_id: selectedContract.id,
        phone: signatureData.phone
      }, { headers });
      
      toast.success('Codes OTP envoyés par SMS et Email');
      setSignatureStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
    setSigningLoading(false);
  };

  const verifySignature = async () => {
    if (!signatureData.sms_code || !signatureData.email_code) {
      toast.error('Les deux codes sont requis');
      return;
    }
    
    setSigningLoading(true);
    try {
      await axios.post(`${API_URL}/api/advertising/contracts/verify-signature`, {
        contract_id: selectedContract.id,
        sms_code: signatureData.sms_code,
        email_code: signatureData.email_code
      }, { headers });
      
      toast.success('Contrat signé avec succès !');
      setShowSignatureModal(false);
      setSignatureStep(1);
      setSignatureData({ phone: '', sms_code: '', email_code: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Codes invalides');
    }
    setSigningLoading(false);
  };

  const openSignatureModal = (contract) => {
    setSelectedContract(contract);
    setSignatureStep(1);
    setSignatureData({ phone: user?.phone || '', sms_code: '', email_code: '' });
    setShowSignatureModal(true);
  };

  const viewStats = async (contract) => {
    try {
      const res = await axios.get(`${API_URL}/api/advertising/contracts/${contract.id}/stats`, { headers });
      setContractStats(res.data);
      setSelectedContract(contract);
      setShowStatsModal(true);
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { color: 'bg-slate-100 text-slate-700', label: 'Brouillon' },
      pending_signature: { color: 'bg-yellow-100 text-yellow-700', label: 'À signer' },
      signed: { color: 'bg-blue-100 text-blue-700', label: 'Signé' },
      pending_payment: { color: 'bg-orange-100 text-orange-700', label: 'En attente paiement' },
      active: { color: 'bg-green-100 text-green-700', label: 'Actif' },
      paused: { color: 'bg-gray-100 text-gray-700', label: 'En pause' },
      completed: { color: 'bg-purple-100 text-purple-700', label: 'Terminé' },
      pending_approval: { color: 'bg-amber-100 text-amber-700', label: 'En validation' },
      approved: { color: 'bg-emerald-100 text-emerald-700', label: 'Approuvé' },
      rejected: { color: 'bg-red-100 text-red-700', label: 'Refusé' }
    };
    const c = config[status] || config.draft;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const totalBudget = contracts.reduce((sum, c) => sum + (c.total_budget || 0), 0);
  const activeContracts = contracts.filter(c => c.status === 'active').length;
  const pendingBanners = banners.filter(b => b.status === 'pending_approval').length;

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8" data-testid="b2b-advertising-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Building2 className="w-8 h-8 text-blue-600" />
            Publicité B2B pour Cliniques
          </h1>
          <p className="text-slate-600 mt-1">
            Développez votre visibilité auprès des patients non-premium
          </p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => setShowCreateContractModal(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            Nouvelle campagne
          </Button>
          <Button onClick={() => setShowCreateBannerModal(true)} variant="outline" className="gap-2">
            <Image className="w-4 h-4" />
            Créer bannière
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <FileSignature className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-900">{contracts.length}</p>
                <p className="text-xs text-blue-700">Contrats</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-900">{activeContracts}</p>
                <p className="text-xs text-green-700">Actifs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                <Euro className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-900">{totalBudget.toFixed(0)}€</p>
                <p className="text-xs text-purple-700">Budget total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                <Image className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-900">{banners.length}</p>
                <p className="text-xs text-amber-700">Bannières ({pendingBanners} en attente)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pricing Info */}
      {pricing && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <MousePointerClick className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-800">
                  CPC: <strong>{pricing.cost_per_click.min}€ - {pricing.cost_per_click.max}€</strong>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Euro className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-800">
                  Budget min: <strong>{pricing.minimum_budget}€</strong>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-800">
                  Positions: {pricing.positions.join(', ')}
                </span>
              </div>
              <Badge className="bg-green-100 text-green-700">
                <Eye className="w-3 h-3 mr-1" />
                Visible uniquement aux non-Premium
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contracts List */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-slate-900">Mes campagnes</h2>
          <Button variant="ghost" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>
        
        {contracts.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-8 text-center">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-medium text-slate-900 mb-2">Aucune campagne</h3>
              <p className="text-slate-600 text-sm mb-4">
                Créez votre première campagne publicitaire pour augmenter votre visibilité
              </p>
              <Button onClick={() => setShowCreateContractModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Créer une campagne
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {contracts.map(contract => (
              <Card key={contract.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-slate-900">{contract.campaign_name}</h3>
                        {getStatusBadge(contract.status)}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Budget:</span>
                          <p className="font-medium">{contract.total_budget}€</p>
                        </div>
                        <div>
                          <span className="text-slate-500">CPC:</span>
                          <p className="font-medium">{contract.cost_per_click}€</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Positions:</span>
                          <p className="font-medium">{contract.banner_positions?.join(', ') || '-'}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Créé:</span>
                          <p className="font-medium">{new Date(contract.created_at).toLocaleDateString('fr')}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 ml-4">
                      {(contract.status === 'draft' || contract.status === 'pending_signature') && (
                        <Button size="sm" onClick={() => openSignatureModal(contract)} className="bg-green-600 hover:bg-green-700">
                          <FileSignature className="w-4 h-4 mr-1" />
                          Signer
                        </Button>
                      )}
                      {contract.status === 'active' && (
                        <Button size="sm" variant="outline" onClick={() => viewStats(contract)}>
                          <BarChart3 className="w-4 h-4 mr-1" />
                          Stats
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Banners List */}
      <div>
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Mes bannières</h2>
        
        {banners.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-8 text-center">
              <Image className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-medium text-slate-900 mb-2">Aucune bannière</h3>
              <p className="text-slate-600 text-sm mb-4">
                Créez des bannières pour vos campagnes publicitaires
              </p>
              <Button variant="outline" onClick={() => setShowCreateBannerModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Créer une bannière
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {banners.map(banner => (
              <Card key={banner.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium">{banner.title?.fr || 'Sans titre'}</h4>
                      <p className="text-sm text-slate-600 line-clamp-2">{banner.content?.fr}</p>
                      <div className="flex gap-2 mt-2">
                        {getStatusBadge(banner.status)}
                        <Badge variant="outline">{banner.position}</Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Contract Modal */}
      <Dialog open={showCreateContractModal} onOpenChange={setShowCreateContractModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Nouvelle campagne publicitaire
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Nom de la campagne *</Label>
              <Input
                value={newContract.campaign_name}
                onChange={(e) => setNewContract({...newContract, campaign_name: e.target.value})}
                placeholder="Ex: Promotion été 2026"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Budget total (€) *</Label>
                <Input
                  type="number"
                  min={50}
                  value={newContract.total_budget}
                  onChange={(e) => setNewContract({...newContract, total_budget: parseFloat(e.target.value)})}
                />
                <p className="text-xs text-slate-500 mt-1">Min: 50€</p>
              </div>
              <div>
                <Label>Coût par clic (€)</Label>
                <Input
                  type="number"
                  min={0.10}
                  max={0.20}
                  step={0.01}
                  value={newContract.cost_per_click}
                  onChange={(e) => setNewContract({...newContract, cost_per_click: parseFloat(e.target.value)})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={newContract.start_date}
                  onChange={(e) => setNewContract({...newContract, start_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Date de fin</Label>
                <Input
                  type="date"
                  value={newContract.end_date}
                  onChange={(e) => setNewContract({...newContract, end_date: e.target.value})}
                />
              </div>
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              Les publicités sont affichées uniquement aux utilisateurs non-premium.
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowCreateContractModal(false)} className="flex-1">
                Annuler
              </Button>
              <Button onClick={handleCreateContract} className="flex-1">
                Créer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Banner Modal */}
      <Dialog open={showCreateBannerModal} onOpenChange={setShowCreateBannerModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="w-5 h-5" />
              Nouvelle bannière
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Titre (FR) *</Label>
              <Input
                value={newBanner.title.fr}
                onChange={(e) => setNewBanner({...newBanner, title: {...newBanner.title, fr: e.target.value}})}
                placeholder="Titre de votre bannière"
              />
            </div>

            <div>
              <Label>Contenu (FR)</Label>
              <Input
                value={newBanner.content.fr}
                onChange={(e) => setNewBanner({...newBanner, content: {...newBanner.content, fr: e.target.value}})}
                placeholder="Description courte"
              />
            </div>

            <div>
              <Label>Lien URL *</Label>
              <Input
                value={newBanner.link_url}
                onChange={(e) => setNewBanner({...newBanner, link_url: e.target.value})}
                placeholder="https://votre-clinique.com"
              />
            </div>

            <div>
              <Label>Position</Label>
              <Select 
                value={newBanner.position} 
                onValueChange={(v) => setNewBanner({...newBanner, position: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="top">Haut de page</SelectItem>
                  <SelectItem value="sidebar">Barre latérale</SelectItem>
                  <SelectItem value="bottom">Bas de page</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="ethics"
                checked={newBanner.ethics_declaration}
                onCheckedChange={(checked) => setNewBanner({...newBanner, ethics_declaration: checked})}
              />
              <label htmlFor="ethics" className="text-sm text-slate-600 cursor-pointer">
                Je déclare que cette publicité respecte les règles éthiques médicales et ne contient pas d'allégations trompeuses.
              </label>
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowCreateBannerModal(false)} className="flex-1">
                Annuler
              </Button>
              <Button onClick={handleCreateBanner} className="flex-1">
                Soumettre
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Signature Modal */}
      <Dialog open={showSignatureModal} onOpenChange={setShowSignatureModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-600" />
              Signature électronique
            </DialogTitle>
          </DialogHeader>

          {selectedContract && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-slate-100 rounded-lg">
                <p className="font-medium">{selectedContract.campaign_name}</p>
                <p className="text-sm text-slate-600">Budget: {selectedContract.total_budget}€</p>
              </div>

              {signatureStep === 1 ? (
                <>
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
                    <p className="font-medium text-yellow-800">Étape 1/2: Envoi des codes</p>
                    <p className="text-yellow-700">Des codes seront envoyés par SMS et Email.</p>
                  </div>

                  <div>
                    <Label className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Téléphone *
                    </Label>
                    <Input
                      value={signatureData.phone}
                      onChange={(e) => setSignatureData({...signatureData, phone: e.target.value})}
                      placeholder="+33612345678"
                    />
                  </div>

                  <Button onClick={initiateSignature} disabled={signingLoading} className="w-full">
                    {signingLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Envoyer les codes
                  </Button>
                </>
              ) : (
                <>
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                    <p className="font-medium text-green-800">Étape 2/2: Vérification</p>
                  </div>

                  <div>
                    <Label className="flex items-center gap-2"><Phone className="w-4 h-4" />Code SMS</Label>
                    <Input
                      value={signatureData.sms_code}
                      onChange={(e) => setSignatureData({...signatureData, sms_code: e.target.value})}
                      placeholder="123456"
                      maxLength={6}
                      className="text-center text-xl tracking-widest"
                    />
                  </div>

                  <div>
                    <Label className="flex items-center gap-2"><Mail className="w-4 h-4" />Code Email</Label>
                    <Input
                      value={signatureData.email_code}
                      onChange={(e) => setSignatureData({...signatureData, email_code: e.target.value})}
                      placeholder="ABCD1234"
                      maxLength={8}
                      className="text-center text-xl tracking-widest"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setSignatureStep(1)} className="flex-1">Retour</Button>
                    <Button onClick={verifySignature} disabled={signingLoading} className="flex-1 bg-green-600 hover:bg-green-700">
                      {signingLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      Signer
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Stats Modal */}
      <Dialog open={showStatsModal} onOpenChange={setShowStatsModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Statistiques: {selectedContract?.campaign_name}
            </DialogTitle>
          </DialogHeader>

          {contractStats && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold">{contractStats.impressions || 0}</p>
                  <p className="text-sm text-slate-600">Impressions</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{contractStats.clicks || 0}</p>
                  <p className="text-sm text-slate-600">Clics</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">{contractStats.ctr?.toFixed(2) || 0}%</p>
                  <p className="text-sm text-slate-600">CTR</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-purple-600">{contractStats.spent?.toFixed(2) || 0}€</p>
                  <p className="text-sm text-slate-600">Dépensé</p>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default B2BAdvertising;
